/**
 * Hotel Search Engine — unified SSOT for agent HOTELS tab.
 *
 * Layers:
 *   hotelSearchCriteria   — original search intent (occupancy, dates, session)
 *   hotelRawResults       — API rows (+ lazy board merge)
 *   hotelNormalizedResults — enriched rows (_filterPrice, _engineOccupancy, …)
 *   hotelActiveFilters    — canonical filter state
 *   hotelFilteredResults  — filtered + sorted display list
 */
import {
  prepareHotelFilterRows,
  applyHotelFilters,
  normalizeHotelFilters,
  computeHotelPriceBounds,
  resetHotelFilters,
} from "../hotelSearchFilters/hotelSearchFilters";
import { resolveHotelSearchOccupancy } from "../../utils/hotelSearch/resolveHotelSearchOccupancy";
import { resolveActiveHotelSearchDates } from "../../utils/hotelSearch/resolveActiveHotelSearchDates";
import {
  HOTEL_FLOW_STEP,
  createHotelFlowPerfMark,
  traceHotelFlowStep,
  traceHotelFlowContext,
} from "../hotelFlowTrace/hotelFlowTrace";

export const HOTEL_ENGINE_TRACE = Object.freeze({
  CRITERIA: "[HOTEL_ENGINE_SEARCH_CRITERIA_TRACE]",
  NORMALIZE: "[HOTEL_ENGINE_NORMALIZE_TRACE]",
  FILTER: "[HOTEL_ENGINE_FILTER_TRACE]",
  CARD_RENDER: "[HOTEL_ENGINE_CARD_RENDER_TRACE]",
  BOOKING_CONTEXT: "[HOTEL_ENGINE_BOOKING_CONTEXT_TRACE]",
  PRICE_FALLBACK: "[HOTEL_ENGINE_PRICE_FALLBACK_TRACE]",
  OCCUPANCY_FALLBACK: "[HOTEL_ENGINE_OCCUPANCY_FALLBACK_TRACE]",
});

export const HOTEL_PRICE_BOUNDS_TRACE = Object.freeze({
  SSOT: "[HOTEL_PRICE_BOUNDS_SSOT_TRACE]",
  FALLBACK: "[HOTEL_PRICE_BOUNDS_FALLBACK_TRACE]",
});

const DEV =
  (typeof import.meta !== "undefined" && import.meta.env?.DEV) ||
  (typeof process !== "undefined" && process.env?.NODE_ENV === "development");

function emitEngineTrace(tag, payload) {
  if (!DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(tag, payload);
  } catch {
    /* ignore */
  }
}

/**
 * Build canonical search criteria from SearchContext-shaped state.
 * Occupancy always prefers roomInfo over stale searchPhrase.
 */
export function buildHotelSearchCriteria(searchState = {}) {
  const {
    roomInfo,
    searchPhrase,
    calendarDateArrival,
    calendarDateLeave,
    location,
    sessionId,
    selectedHotelCurrency,
    searchTab,
    lastSearchPayload,
    currentSearchRequestId,
  } = searchState;

  const activeDates = resolveActiveHotelSearchDates({
    calendarDateArrival,
    calendarDateLeave,
    searchPhrase,
    lastSearchPayload,
    currentSearchRequestId,
  });

  const occupancyResolved = resolveHotelSearchOccupancy({
    searchState: { roomInfo, calendarDateArrival, calendarDateLeave },
    searchPayload: { searchPhrase },
    trace: false,
  });

  const criteria = {
    occupancy: {
      rooms: occupancyResolved.cardDisplayedRooms,
      adults: occupancyResolved.cardDisplayedAdults,
      children: occupancyResolved.cardDisplayedChildren,
      infants: occupancyResolved.infants ?? 0,
      nights: occupancyResolved.nights,
      passengers: occupancyResolved.passengers,
      source: occupancyResolved.sourceUsedForDisplay,
    },
    rooms: occupancyResolved.roomsSource,
    checkIn: activeDates.checkIn,
    checkOut: activeDates.checkOut,
    sessionId: sessionId ?? searchPhrase?.session ?? null,
    currency: selectedHotelCurrency ?? searchPhrase?.currency ?? null,
    searchTab: searchTab ?? null,
    locationSummary:
      location && typeof location === "object"
        ? {
            city:
              location.CITYNAME ||
              location.CITYNAME_HEB ||
              location.city ||
              null,
            country:
              location.COUNTRYNAME ||
              location.COUNTRYNAME_HEB ||
              location.country ||
              null,
            hotelName: location.hotelserched || location.hotelName || null,
          }
        : null,
  };

  emitEngineTrace(HOTEL_ENGINE_TRACE.CRITERIA, criteria);
  return criteria;
}

/** Merge lazy board fetch data into raw API rows. */
export function mergeHotelBoardsIntoRaw(rawHotels, boardsById = {}) {
  const source = Array.isArray(rawHotels) ? rawHotels : [];
  return source.map((hotel) => {
    const hotelId = hotel?.ID;
    const boards = hotel?.boards || (hotelId != null ? boardsById?.[hotelId] : null);
    if (!boards?.length) return hotel;
    return { ...hotel, boards };
  });
}

/**
 * Enrich raw rows with filter/pricing fields and attach engine occupancy VM.
 * Cards must read _engineOccupancy / _engineCardVm — never recompute locally.
 */
export function normalizeHotelSearchEngineResults(rawHotels, criteria, pricingContext = {}) {
  const prepared = prepareHotelFilterRows(rawHotels, pricingContext);
  const occupancy = criteria?.occupancy ?? null;

  const normalized = prepared.map((row) => {
    const engineCardVm = {
      occupancy,
      displayPrice: row._filterPrice ?? null,
      sortDisplayPrice: row._sortDisplayPrice ?? row._filterPrice ?? null,
    };
    return {
      ...row,
      _engineOccupancy: occupancy,
      _engineCardVm: engineCardVm,
    };
  });

  emitEngineTrace(HOTEL_ENGINE_TRACE.NORMALIZE, {
    rawCount: rawHotels?.length ?? 0,
    normalizedCount: normalized.length,
    criteriaOccupancy: occupancy,
    priceBounds: computeHotelPriceBounds(normalized),
    sampleIds: normalized.slice(0, 3).map((r) => r?.ID),
  });

  return normalized;
}

/**
 * Full engine pipeline for HOTELS results page.
 */
export function runHotelSearchEngine({
  rawResults,
  filtersState,
  pricingContext,
  boardsById,
  searchState,
  trace = true,
}) {
  const perf = createHotelFlowPerfMark("hotel_engine_pipeline", {
    rawCount: Array.isArray(rawResults) ? rawResults.length : 0,
  });
  traceHotelFlowStep(HOTEL_FLOW_STEP.FILTERS_SORT, { phase: "start" });

  const criteria = buildHotelSearchCriteria(searchState);
  const hotelRawResults = mergeHotelBoardsIntoRaw(rawResults, boardsById);
  const hotelNormalizedResults = normalizeHotelSearchEngineResults(
    hotelRawResults,
    criteria,
    pricingContext
  );
  const priceBounds = computeHotelPriceBounds(hotelNormalizedResults);
  const hotelActiveFilters = normalizeHotelFilters(filtersState, priceBounds);

  const { hotels: hotelFilteredResults, meta: filterMeta } = applyHotelFilters(
    hotelNormalizedResults,
    hotelActiveFilters,
    { priceBounds, trace: false }
  );

  const meta = {
    ...filterMeta,
    priceBounds,
    criteriaOccupancy: criteria.occupancy,
  };

  if (trace) {
    emitEngineTrace(HOTEL_ENGINE_TRACE.FILTER, {
      originalCount: meta.originalCount,
      filteredCount: meta.filteredCount,
      activeFilters: meta.activeFilters,
      sortMode: meta.sortMode,
      selectedPriceFilter: meta.selectedPriceFilter,
      resolvedPriceRange: meta.resolvedPriceRange,
      reasonIfEmpty: meta.reasonIfEmpty,
      criteriaOccupancy: criteria.occupancy,
    });
  }

  perf.end({
    normalizedCount: hotelNormalizedResults.length,
    filteredCount: hotelFilteredResults.length,
  });
  traceHotelFlowStep(HOTEL_FLOW_STEP.FILTERS_SORT, {
    phase: "done",
    originalCount: meta.originalCount,
    filteredCount: hotelFilteredResults.length,
  });

  return {
    hotelSearchCriteria: criteria,
    hotelRawResults,
    hotelNormalizedResults,
    hotelActiveFilters,
    hotelFilteredResults,
    meta,
  };
}

/** Read precomputed card VM from normalized/filtered row. */
export function readHotelEngineCardVm(hotelRow) {
  if (hotelRow?._engineCardVm) return hotelRow._engineCardVm;
  if (hotelRow?._engineOccupancy || hotelRow?._filterPrice != null) {
    return {
      occupancy: hotelRow._engineOccupancy ?? null,
      displayPrice: hotelRow._filterPrice ?? null,
      sortDisplayPrice: hotelRow._sortDisplayPrice ?? hotelRow._filterPrice ?? null,
    };
  }
  return null;
}

/** Resolve occupancy for card display — SSOT row first, then criteria fallback. */
export function resolveHotelEngineCardOccupancy(hotelRow, hotelSearchCriteria, options = {}) {
  const { trace = true, traceContext = {} } = options;
  const vm = readHotelEngineCardVm(hotelRow);
  const rowOccupancy = hotelRow?._engineOccupancy ?? vm?.occupancy ?? null;

  if (rowOccupancy) {
    return { ...rowOccupancy, _displaySource: "engineOccupancy" };
  }

  const criteriaOcc = hotelSearchCriteria?.occupancy ?? null;
  if (criteriaOcc) {
    if (trace) {
      emitEngineTrace(HOTEL_ENGINE_TRACE.OCCUPANCY_FALLBACK, {
        hotelId: hotelRow?.ID ?? hotelRow?.Id ?? null,
        fallback: "hotelSearchCriteria.occupancy",
        criteriaOccupancy: criteriaOcc,
        ...traceContext,
      });
    }
    return { ...criteriaOcc, _displaySource: "criteriaFallback" };
  }

  if (trace) {
    emitEngineTrace(HOTEL_ENGINE_TRACE.OCCUPANCY_FALLBACK, {
      hotelId: hotelRow?.ID ?? hotelRow?.Id ?? null,
      fallback: "none",
      ...traceContext,
    });
  }
  return null;
}

/**
 * Card display price SSOT — prefers _engineCardVm.displayPrice (matches filter pipeline).
 * Temporary hook fallback with trace when engine field is missing.
 * @param {object|null} hotelRow
 * @param {object|null} hookPriceVm — output of useAgentHotelSearchResultPriceVm
 * @param {{ displayCurrency?: string|null, trace?: boolean, traceContext?: object }} [options]
 */
export function resolveHotelEngineCardDisplayPrice(hotelRow, hookPriceVm, options = {}) {
  const { displayCurrency = null, trace = true, traceContext = {} } = options;
  const vm = readHotelEngineCardVm(hotelRow);
  const enginePrice = vm?.displayPrice ?? hotelRow?._filterPrice ?? null;
  const parsedEngine =
    enginePrice != null && Number.isFinite(Number(enginePrice)) ? Number(enginePrice) : null;

  if (parsedEngine != null) {
    return {
      finalPrice: parsedEngine,
      displayCurrency:
        displayCurrency ?? hookPriceVm?.displayCurrency ?? null,
      source: "engineCardVm",
    };
  }

  const hookPrice =
    hookPriceVm?.finalPrice != null && Number.isFinite(Number(hookPriceVm.finalPrice))
      ? Number(hookPriceVm.finalPrice)
      : null;

  if (trace && hookPrice != null) {
    emitEngineTrace(HOTEL_ENGINE_TRACE.PRICE_FALLBACK, {
      hotelId: hotelRow?.ID ?? hotelRow?.Id ?? null,
      enginePrice: parsedEngine,
      hookPrice,
      hookSource: "useAgentHotelSearchResultPriceVm",
      ...traceContext,
    });
  }

  return {
    finalPrice: hookPrice,
    displayCurrency: hookPriceVm?.displayCurrency ?? displayCurrency ?? null,
    source: hookPrice != null ? "hookFallback" : "none",
  };
}

/** DEV trace when a card/list row renders. */
export function traceHotelEngineCardRender(hotelRow, context = {}) {
  const vm = readHotelEngineCardVm(hotelRow);
  traceHotelFlowStep(HOTEL_FLOW_STEP.CARDS_RENDER, {
    hotelId: hotelRow?.ID ?? hotelRow?.Id ?? null,
    displayPrice: vm?.displayPrice ?? hotelRow?._filterPrice ?? null,
    ...context,
  });
  emitEngineTrace(HOTEL_ENGINE_TRACE.CARD_RENDER, {
    hotelId: hotelRow?.ID ?? hotelRow?.Id ?? null,
    displayName: hotelRow?.DisplayName ?? hotelRow?.HotelName ?? null,
    engineOccupancy: vm?.occupancy ?? hotelRow?._engineOccupancy ?? null,
    displayPrice: vm?.displayPrice ?? hotelRow?._filterPrice ?? null,
    ...context,
  });
}

/** DEV trace for profile / booking navigation context. */
export function traceHotelEngineBookingContext(payload = {}) {
  traceHotelFlowContext(payload.stage ?? "booking", payload);
  emitEngineTrace(HOTEL_ENGINE_TRACE.BOOKING_CONTEXT, payload);
}

/** True when engine.meta.priceBounds has a usable min/max span. */
export function isValidHotelEnginePriceBounds(bounds) {
  if (!bounds || typeof bounds !== "object") return false;
  const min = Number(bounds.min);
  const max = Number(bounds.max);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return false;
  if (bounds.count != null && Number(bounds.count) === 0) return false;
  return max >= min;
}

/** Slider endpoints — pad when min === max (same UX as legacy drawer). */
export function hotelPriceBoundsToSliderRange(bounds) {
  const rawMin = Number(bounds?.min);
  const rawMax = Number(bounds?.max);
  if (!Number.isFinite(rawMin) || !Number.isFinite(rawMax)) {
    return [1, 1_000_000];
  }
  let min = rawMin;
  let max = rawMax;
  if (min > max) {
    min = rawMax;
    max = rawMin;
  }
  if (min === max) {
    const pad = Math.max(1, Math.abs(min) * 0.05);
    return [Math.max(0, min - pad), max + pad];
  }
  return [min, max];
}

/**
 * Filter drawer / slider price bounds SSOT for HOTELS tab.
 * @param {{ enginePriceBounds?: object|null, fallbackCompute?: () => [number, number], trace?: boolean, traceContext?: object }} input
 */
export function resolveHotelFilterPriceBounds(input = {}) {
  const {
    enginePriceBounds = null,
    fallbackCompute = () => [1, 1_000_000],
    trace = true,
    traceContext = {},
  } = input;

  if (isValidHotelEnginePriceBounds(enginePriceBounds)) {
    const resolved = {
      min: Number(enginePriceBounds.min),
      max: Number(enginePriceBounds.max),
      count: enginePriceBounds.count ?? null,
      source: "engine",
    };
    if (trace) {
      emitEngineTrace(HOTEL_PRICE_BOUNDS_TRACE.SSOT, {
        ...resolved,
        sliderRange: hotelPriceBoundsToSliderRange(resolved),
        ...traceContext,
      });
    }
    return resolved;
  }

  const fallbackRange = fallbackCompute();
  const min = Number(fallbackRange?.[0]);
  const max = Number(fallbackRange?.[1]);
  const resolved = {
    min: Number.isFinite(min) ? min : 1,
    max: Number.isFinite(max) ? max : 1_000_000,
    count: null,
    source: "fallback",
  };
  if (trace) {
    emitEngineTrace(HOTEL_PRICE_BOUNDS_TRACE.FALLBACK, {
      ...resolved,
      enginePriceBounds,
      sliderRange: hotelPriceBoundsToSliderRange(resolved),
      ...traceContext,
    });
  }
  return resolved;
}

export { resetHotelFilters };
